import { resolveErrorText } from '@/api/errors';
import { openapiClient, unwrapResult } from '@/api/openapi-client';
import { useReservationList } from '@/features/reservations/hooks/useReservations';
import { useErrorT } from '@/i18n';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { todayString } from '@/utils/datetime';
import type {
  AddSeatBody,
  AssignItemSeatBody,
  AttachReservationBody,
  CancelRoundBody,
  ForceCloseBody,
  LinkSessionCustomerBody,
  MergeSessionBody,
  MoveSessionBody,
  RequestBillBody,
  SeatReservationBody,
  SplitBillBody,
  StaffOpenSessionBody,
  SubmitRoundBody,
  TableOrderingConfigBody,
  TableSession,
  TakePaymentBody,
  VoidItemBody,
} from '@restaurant/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { f7 } from 'framework7-react';

// ─── Query keys ──────────────────────────────────────────────────────────────

export const toKeys = {
  tables: (r: string) => ['table-ordering', r, 'tables'] as const,
  session: (r: string, s: string) => ['table-ordering', r, 'session', s] as const,
  history: (r: string, date: string) => ['table-ordering', r, 'history', date] as const,
  config: (r: string) => ['table-ordering', r, 'config'] as const,
  menu: (r: string) => ['table-ordering', r, 'dine-in-menu'] as const,
};

function useRid(): string {
  return useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
}

// ─── Live table view ─────────────────────────────────────────────────────────

/** All table states for the live view. Polls every 10s so the floor stays
 *  current without a manual refresh (mirrors the orders pending poll). */
export function useTableStates() {
  const restaurantId = useRid();
  return useQuery({
    queryKey: toKeys.tables(restaurantId),
    queryFn: async () => {
      return unwrapResult(
        await openapiClient.GET('/api/restaurants/{restaurantId}/table-ordering/tables', {
          params: { path: { restaurantId } },
        }),
      );
    },
    enabled: !!restaurantId,
    refetchInterval: 1000 * 10,
  });
}

/** A single session in full detail. Polls every 8s while mounted so an open
 *  tab reflects guest-submitted rounds / service calls live. */
export function useTableSession(sessionId: string | null) {
  const restaurantId = useRid();
  return useQuery({
    queryKey: toKeys.session(restaurantId, sessionId ?? ''),
    queryFn: async () => {
      return unwrapResult(
        await openapiClient.GET(
          '/api/restaurants/{restaurantId}/table-ordering/sessions/{sessionId}',
          { params: { path: { restaurantId, sessionId: sessionId ?? '' } } },
        ),
      );
    },
    enabled: !!restaurantId && !!sessionId,
    refetchInterval: 1000 * 8,
  });
}

/** DINE_IN menu for the staff order pad - same public menu the guest sees,
 *  resolved for the dine-in channel. */
export function useDineInMenu() {
  const restaurantId = useRid();
  return useQuery({
    queryKey: toKeys.menu(restaurantId),
    queryFn: async () => {
      const result = await openapiClient.GET('/api/restaurants/{restaurantId}/customer-menu', {
        params: { path: { restaurantId }, query: { lang: 'en', channel: 'DINE_IN' } },
      });
      if (result.response.status === 404) return null;
      return unwrapResult(result);
    },
    enabled: !!restaurantId,
    staleTime: 1000 * 60 * 5,
  });
}

/** Closed tabs for one local day - the staff History view. `date` is a
 *  YYYY-MM-DD string in restaurant-local time; the backend resolves the day
 *  boundary in the restaurant's timezone. */
export function useSessionHistory(date: string) {
  const restaurantId = useRid();
  return useQuery({
    queryKey: toKeys.history(restaurantId, date),
    queryFn: async () => {
      return unwrapResult(
        await openapiClient.GET('/api/restaurants/{restaurantId}/table-ordering/session-history', {
          params: { path: { restaurantId }, query: { date } },
        }),
      );
    },
    enabled: !!restaurantId,
  });
}

/** Today's unseated, table-assigned bookings - the "Expected arrivals" for the
 *  Tables tab. Reuses the reservation list; filters to CONFIRMED/PENDING that
 *  aren't yet arrived/cancelled and have a table. Soonest first. */
export function useExpectedArrivals() {
  const q = useReservationList(todayString());
  const arrivals = (q.data?.data ?? [])
    .filter(
      (r) =>
        (r.status === 'CONFIRMED' || r.status === 'PENDING') &&
        !r.cancelledAt &&
        (r.arrivalStatus === null || r.arrivalStatus === 'WAITING') &&
        r.tableIds.length > 0,
    )
    .sort((a, b) => a.time.localeCompare(b.time));
  return { arrivals, isLoading: q.isLoading };
}

// ─── Config ──────────────────────────────────────────────────────────────────

export function useTableOrderingConfig() {
  const restaurantId = useRid();
  return useQuery({
    queryKey: toKeys.config(restaurantId),
    queryFn: async () => {
      return unwrapResult(
        await openapiClient.GET('/api/restaurants/{restaurantId}/table-ordering/config', {
          params: { path: { restaurantId } },
        }),
      );
    },
    enabled: !!restaurantId,
  });
}

export function useUpdateTableOrderingConfig() {
  const restaurantId = useRid();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: TableOrderingConfigBody) =>
      unwrapResult(
        await openapiClient.PUT('/api/restaurants/{restaurantId}/table-ordering/config', {
          params: { path: { restaurantId } },
          body,
        }),
      ),
    onSuccess: (cfg) => qc.setQueryData(toKeys.config(restaurantId), cfg),
  });
}

// ─── Session mutations ───────────────────────────────────────────────────────
//
// Every mutation returns the full updated TableSession. We push it into the
// session cache AND invalidate the live table list so the floor reflects the
// change immediately (totals, state, badges).

type SessionResult = { data?: TableSession; error?: unknown; response: Response };

function useSessionMutation<TArgs>(
  run: (restaurantId: string, args: TArgs) => Promise<SessionResult>,
) {
  const restaurantId = useRid();
  const qc = useQueryClient();
  const errorMessages = useErrorT();
  return useMutation({
    mutationFn: async (args: TArgs) => unwrapResult(await run(restaurantId, args)),
    onSuccess: (session) => {
      qc.setQueryData(toKeys.session(restaurantId, session.id), session);
      qc.invalidateQueries({ queryKey: toKeys.tables(restaurantId) });
    },
    // Surface every failure as a non-blocking toast - without this a rejected
    // mutation (409 unfinished round, 403, validation…) silently does nothing.
    onError: (err) => {
      f7.toast.show({
        text: resolveErrorText(err, errorMessages),
        closeTimeout: 3500,
        position: 'bottom',
        destroyOnClose: true,
      });
    },
  });
}

export const useOpenSession = () =>
  useSessionMutation((restaurantId, body: StaffOpenSessionBody) =>
    openapiClient.POST('/api/restaurants/{restaurantId}/table-ordering/sessions', {
      params: { path: { restaurantId } },
      body,
    }),
  );

export const useStaffSubmitRound = () =>
  useSessionMutation(
    (restaurantId, { sessionId, body }: { sessionId: string; body: SubmitRoundBody }) =>
      openapiClient.POST(
        '/api/restaurants/{restaurantId}/table-ordering/sessions/{sessionId}/rounds',
        {
          params: { path: { restaurantId, sessionId } },
          body,
        },
      ),
  );

export const useFireRound = () =>
  useSessionMutation(
    (restaurantId, { sessionId, roundId }: { sessionId: string; roundId: string }) =>
      openapiClient.POST(
        '/api/restaurants/{restaurantId}/table-ordering/sessions/{sessionId}/rounds/{roundId}/fire',
        {
          params: { path: { restaurantId, sessionId, roundId } },
        },
      ),
  );

export const useAcceptRound = () =>
  useSessionMutation(
    (restaurantId, { sessionId, roundId }: { sessionId: string; roundId: string }) =>
      openapiClient.POST(
        '/api/restaurants/{restaurantId}/table-ordering/sessions/{sessionId}/rounds/{roundId}/accept',
        {
          params: { path: { restaurantId, sessionId, roundId } },
        },
      ),
  );

export const useMarkRoundReady = () =>
  useSessionMutation(
    (restaurantId, { sessionId, roundId }: { sessionId: string; roundId: string }) =>
      openapiClient.POST(
        '/api/restaurants/{restaurantId}/table-ordering/sessions/{sessionId}/rounds/{roundId}/ready',
        {
          params: { path: { restaurantId, sessionId, roundId } },
        },
      ),
  );

export const useServeRound = () =>
  useSessionMutation(
    (restaurantId, { sessionId, roundId }: { sessionId: string; roundId: string }) =>
      openapiClient.POST(
        '/api/restaurants/{restaurantId}/table-ordering/sessions/{sessionId}/rounds/{roundId}/serve',
        {
          params: { path: { restaurantId, sessionId, roundId } },
        },
      ),
  );

export const useReopenRound = () =>
  useSessionMutation(
    (restaurantId, { sessionId, roundId }: { sessionId: string; roundId: string }) =>
      openapiClient.POST(
        '/api/restaurants/{restaurantId}/table-ordering/sessions/{sessionId}/rounds/{roundId}/reopen',
        {
          params: { path: { restaurantId, sessionId, roundId } },
        },
      ),
  );

export const useCancelRound = () =>
  useSessionMutation(
    (
      restaurantId,
      { sessionId, roundId, body }: { sessionId: string; roundId: string; body: CancelRoundBody },
    ) =>
      openapiClient.POST(
        '/api/restaurants/{restaurantId}/table-ordering/sessions/{sessionId}/rounds/{roundId}/cancel',
        {
          params: { path: { restaurantId, sessionId, roundId } },
          body,
        },
      ),
  );

export const useVoidItem = () =>
  useSessionMutation(
    (
      restaurantId,
      { sessionId, itemId, body }: { sessionId: string; itemId: string; body: VoidItemBody },
    ) =>
      openapiClient.POST(
        '/api/restaurants/{restaurantId}/table-ordering/sessions/{sessionId}/items/{itemId}/void',
        {
          params: { path: { restaurantId, sessionId, itemId } },
          body,
        },
      ),
  );

export const useRestoreItem = () =>
  useSessionMutation((restaurantId, { sessionId, itemId }: { sessionId: string; itemId: string }) =>
    openapiClient.POST(
      '/api/restaurants/{restaurantId}/table-ordering/sessions/{sessionId}/items/{itemId}/restore',
      {
        params: { path: { restaurantId, sessionId, itemId } },
      },
    ),
  );

export const useAddSeat = () =>
  useSessionMutation(
    (restaurantId, { sessionId, body }: { sessionId: string; body: AddSeatBody }) =>
      openapiClient.POST(
        '/api/restaurants/{restaurantId}/table-ordering/sessions/{sessionId}/seats',
        {
          params: { path: { restaurantId, sessionId } },
          body,
        },
      ),
  );

export const useAssignItemSeat = () =>
  useSessionMutation(
    (
      restaurantId,
      { sessionId, itemId, body }: { sessionId: string; itemId: string; body: AssignItemSeatBody },
    ) =>
      openapiClient.PATCH(
        '/api/restaurants/{restaurantId}/table-ordering/sessions/{sessionId}/items/{itemId}/seat',
        {
          params: { path: { restaurantId, sessionId, itemId } },
          body,
        },
      ),
  );

export const useAttachReservation = () =>
  useSessionMutation(
    (restaurantId, { sessionId, body }: { sessionId: string; body: AttachReservationBody }) =>
      openapiClient.POST(
        '/api/restaurants/{restaurantId}/table-ordering/sessions/{sessionId}/attach-reservation',
        {
          params: { path: { restaurantId, sessionId } },
          body,
        },
      ),
  );

export const useSeatReservation = () =>
  useSessionMutation((restaurantId, body: SeatReservationBody) =>
    openapiClient.POST('/api/restaurants/{restaurantId}/table-ordering/sessions/seat', {
      params: { path: { restaurantId } },
      body,
    }),
  );

export const useLinkCustomer = () =>
  useSessionMutation(
    (restaurantId, { sessionId, body }: { sessionId: string; body: LinkSessionCustomerBody }) =>
      openapiClient.POST(
        '/api/restaurants/{restaurantId}/table-ordering/sessions/{sessionId}/customer',
        {
          params: { path: { restaurantId, sessionId } },
          body,
        },
      ),
  );

export const useUnlinkCustomer = () =>
  useSessionMutation((restaurantId, { sessionId }: { sessionId: string }) =>
    openapiClient.DELETE(
      '/api/restaurants/{restaurantId}/table-ordering/sessions/{sessionId}/customer',
      {
        params: { path: { restaurantId, sessionId } },
      },
    ),
  );

export const useResolveServiceRequest = () =>
  useSessionMutation(
    (restaurantId, { sessionId, requestId }: { sessionId: string; requestId: string }) =>
      openapiClient.POST(
        '/api/restaurants/{restaurantId}/table-ordering/sessions/{sessionId}/service-requests/{requestId}/resolve',
        {
          params: { path: { restaurantId, sessionId, requestId } },
        },
      ),
  );

export const useStaffRequestBill = () =>
  useSessionMutation(
    (restaurantId, { sessionId, body }: { sessionId: string; body: RequestBillBody }) =>
      openapiClient.POST(
        '/api/restaurants/{restaurantId}/table-ordering/sessions/{sessionId}/request-bill',
        {
          params: { path: { restaurantId, sessionId } },
          body,
        },
      ),
  );

export const useTakePayment = () =>
  useSessionMutation(
    (restaurantId, { sessionId, body }: { sessionId: string; body: TakePaymentBody }) =>
      openapiClient.POST(
        '/api/restaurants/{restaurantId}/table-ordering/sessions/{sessionId}/payments',
        {
          params: { path: { restaurantId, sessionId } },
          body,
        },
      ),
  );

export const useSplitBill = () =>
  useSessionMutation(
    (restaurantId, { sessionId, body }: { sessionId: string; body: SplitBillBody }) =>
      openapiClient.POST(
        '/api/restaurants/{restaurantId}/table-ordering/sessions/{sessionId}/split',
        {
          params: { path: { restaurantId, sessionId } },
          body,
        },
      ),
  );

export const useMoveSession = () =>
  useSessionMutation(
    (restaurantId, { sessionId, body }: { sessionId: string; body: MoveSessionBody }) =>
      openapiClient.POST(
        '/api/restaurants/{restaurantId}/table-ordering/sessions/{sessionId}/move',
        {
          params: { path: { restaurantId, sessionId } },
          body,
        },
      ),
  );

export const useMergeSession = () =>
  useSessionMutation(
    (restaurantId, { sessionId, body }: { sessionId: string; body: MergeSessionBody }) =>
      openapiClient.POST(
        '/api/restaurants/{restaurantId}/table-ordering/sessions/{sessionId}/merge',
        {
          params: { path: { restaurantId, sessionId } },
          body,
        },
      ),
  );

export const useForceClose = () =>
  useSessionMutation(
    (restaurantId, { sessionId, body }: { sessionId: string; body: ForceCloseBody }) =>
      openapiClient.POST(
        '/api/restaurants/{restaurantId}/table-ordering/sessions/{sessionId}/force-close',
        {
          params: { path: { restaurantId, sessionId } },
          body,
        },
      ),
  );

export const useCloseSession = () =>
  useSessionMutation((restaurantId, { sessionId }: { sessionId: string }) =>
    openapiClient.POST(
      '/api/restaurants/{restaurantId}/table-ordering/sessions/{sessionId}/close',
      {
        params: { path: { restaurantId, sessionId } },
      },
    ),
  );
